import _ from 'lodash'
import type { ViewerPlatformEssentials } from '@wix/fe-essentials-viewer-platform'
import type { ControllerDataAPI, LivePreviewOptions, IModelsAPI, ClientSpecMapAPI } from '@wix/thunderbolt-symbols'
import type { IWixSelector } from './wixSelector'
import type { ISetPropsManager } from './setPropsManager'
import type { ISlotsManager } from './slotsManager'
import type { ControllerData, IComponentSdksManager } from '../types'
import type { IAppsUrls } from './appsUrls'
import type { CreateSingleRepeatedControllerFn } from './applications'
import type { ComponentSdkFactory } from '@wix/thunderbolt-platform-types'
import { extractArtifactInfoFromScriptUrl } from '../appsAPI/extractArtifactInfoFromScriptUrl'
import {
	APPS_URLS,
	BOOTSTRAP_DATA,
	CLIENT_SPEC_MAP_API,
	COMPONENT_SDKS_MANAGER,
	CONTROLLER_PARAMS,
	MODELS_API,
	PLATFORM_ESSENTIALS,
	SET_PROPS_MANAGER,
	SLOTS_MANAGER,
	WIX_SELECTOR,
} from './moduleNames'
import type { AppState, BootstrapData } from '../../types'

export type ControllersParams = Array<{ controllerCompId: string; controllerParams: ControllerDataAPI }>

export type IControllerParams = {
	createControllersParams: (
		appDefinitionId: string,
		controllersData: Array<ControllerData>,
		appState: AppState,
		createSingleRepeatedController: CreateSingleRepeatedControllerFn,
		livePreviewOptions?: Partial<LivePreviewOptions>,
		isRepeatedSlots?: boolean
	) => ControllersParams
}

const ControllerParams = (
	modelsApi: IModelsAPI,
	{ createSetPropsForOOI }: ISetPropsManager,
	wixSelector: IWixSelector,
	slotsManager: ISlotsManager,
	clientSpecMapApi: ClientSpecMapAPI,
	bootstrapData: BootstrapData,
	essentials: ViewerPlatformEssentials,
	appsUrls: IAppsUrls,
	componentSdksManager: IComponentSdksManager
): IControllerParams => {
	const getSlotByName = modelsApi.getSlotByName
	const {
		platformEnvData: {
			window: { csrfToken },
		},
	} = bootstrapData

	const createControllersParams: IControllerParams['createControllersParams'] = (
		appDefinitionId,
		controllersData,
		appState,
		createSingleRepeatedController,
		livePreviewOptions?,
		isRepeatedSlots?
	) => {
		const allConnections = modelsApi.getAllConnections()
		const widgetsClientSpecMapData = clientSpecMapApi.getWidgetsClientSpecMapData(appDefinitionId)
		const { appParams, wixCodeApi, platformAppServicesApi, platformApi, platformApiProvider } = appState
		const appEssentials = platformAppServicesApi.essentials

		return controllersData.map((controllerData) => {
			const { controllerType, compId, templateId, config, externalId, context, appDefinitionId } = controllerData
			const builderType = config.builderType
			const $wScope = context
				? wixSelector.create$wRepeaterScope({
						compId: context._internal.repeaterCompId,
						itemId: context.itemId,
					})
				: wixSelector.create$wGlobalScope()

			const clientSpecMapData = widgetsClientSpecMapData[controllerType]

			const controllerScriptUrl = appsUrls.getControllerScriptUrl(appDefinitionId, controllerType)

			const { artifactId, artifactVersion } = extractArtifactInfoFromScriptUrl(controllerScriptUrl)

			let connections = _.flatMap(allConnections[compId], _.values)
			let type = controllerType
			let $w = context
				? wixSelector.create$w(compId, wixCodeApi.telemetry).at(context, isRepeatedSlots)
				: wixSelector.create$w(compId, wixCodeApi.telemetry)

			const getSlot = (slotName: string, path?: string[]) =>
				slotsManager.getSlot(compId, compId, slotName, wixSelector.getInstance, $wScope, undefined, path)

			const builderBOBConnections = componentSdksManager.getBuilderBOBConnections(builderType)

			if (builderBOBConnections) {
				connections = builderBOBConnections.connections
				type = `${controllerType}-${builderBOBConnections.pageId}`
				$w = componentSdksManager.getBuilderBOB$w(compId, getSlot)
			}

			return {
				controllerCompId: compId,
				controllerParams: {
					$w,
					getSlot,
					compId: templateId || compId,
					name: clientSpecMapData?.widgetName || controllerType,
					componentFields: clientSpecMapData?.componentFields || {},
					type,
					config,
					connections,
					warmupData: null,
					appParams,
					platformAPIs: Object.assign(platformApi, platformAppServicesApi),
					wixCodeApi,
					csrfToken,
					setProps: createSetPropsForOOI(compId, context),
					externalId,
					essentials: essentials.createControllerEssentials(
						{
							widgetId: controllerType,
							compId,
							artifactId,
							artifactVersion,
						},
						appEssentials
					),
					livePreviewOptions,
					platformApiProvider,
					createRepeatedSlotAPI: (slotName: string, repeatedItemId: string, path?: string[]) => {
						const slotId = getSlotByName(compId, slotName, path)
						if (!slotId) {
							return Promise.resolve({})
						}

						return createSingleRepeatedController(slotName, slotId, repeatedItemId).then(
							async (runControllersOnReadys: Function) => {
								await runControllersOnReadys()
								const local$wScope = wixSelector.create$wRepeaterScope({ compId: slotId, itemId: repeatedItemId })
								return slotsManager.getSlot(
									compId,
									compId,
									slotName,
									wixSelector.getInstance,
									local$wScope,
									repeatedItemId,
									path
								)
							}
						)
					},
				},
			}
		})
	}

	return {
		createControllersParams,
	}
}

export default {
	factory: ControllerParams,
	deps: [
		MODELS_API,
		SET_PROPS_MANAGER,
		WIX_SELECTOR,
		SLOTS_MANAGER,
		CLIENT_SPEC_MAP_API,
		BOOTSTRAP_DATA,
		PLATFORM_ESSENTIALS,
		APPS_URLS,
		COMPONENT_SDKS_MANAGER,
	],
	name: CONTROLLER_PARAMS,
}
